import { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
    Popper,
    Paper,
    Typography,
    Badge,
    IconButton,
    ClickAwayListener,
    Stack,
    Box,
    Fab,
} from "@mui/material";
import Grow from "@mui/material/Grow";
import useMediaQuery from "@mui/material/useMediaQuery";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import { selectHelpFab } from "../../features/layout/helpFabSlice";
import { WhatsApp } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import rules from "./messages.json";

const PINK = "#E17E7F"; // TO DO use website theme secondary color

function normalize(p) {
    if (!p) return "/";
    return p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;
}

function findRule(pathname) {
    const path = normalize(pathname);
    if (rules[path]) return rules[path];
}

/** minimal interesting: just a small pink dot + soft halo */
const HelpFab = styled(Fab)(({ theme }) => ({
    position: "relative",
    overflow: "visible",
    borderRadius: 16,
}));

const PopupCard = styled(Paper)(({ theme }) => {
    const main = theme.palette.primary.main;
    const dark = theme.palette.primary.dark;
    const fg = theme.palette.getContrastText(main);

    return {
        position: "relative",
        borderRadius: 14,
        padding: "12px 14px",
        width: "min(92vw, 360px)",
        overflow: "hidden",
        background: main,
        color: fg,
        border: `1px solid ${dark}`,
        boxShadow: theme.shadows[6],

        "& .MuiTypography-root": { color: "inherit" },

        "& .MuiPopper-arrow": {
            position: "absolute",
            width: 12,
            height: 12,
            "&::before": {
                content: '""',
                position: "absolute",
                width: "100%",
                height: "100%",
                transform: "rotate(45deg)",
                background: main,
                border: `1px solid ${dark}`,
                boxShadow: theme.shadows[2],
            },
        },

        '[data-popper-placement^="top"] & .MuiPopper-arrow': {
            bottom: -6,
            left: "auto",
            right: 14,
            "&::before": {
                borderTop: "none",
                borderLeft: "none",
            },
        },
    };
});

const CtaLink = styled("a")(() => ({
    borderRadius: 12,
    backgroundColor: PINK,
    border: `1px solid ${PINK}`,
    color: "#fff",
    boxShadow: "0 10px 22px rgba(255,79,184,0.28)",
    textDecoration: "none",
    transition: "transform 160ms ease, box-shadow 160ms ease, filter 160ms ease",

    "&:hover, &:focus, &:focus-visible": {
        backgroundColor: `${PINK} !important`,
        borderColor: `${PINK} !important`,
        color: "#fff",
        transform: "translateY(-1px)",
        boxShadow: "0 14px 26px rgba(255,79,184,0.34)",
        filter: "brightness(1.02)",
        textDecoration: "none",
    },

    "&:active": {
        backgroundColor: PINK,
        borderColor: PINK,
        color: "#fff",
        transform: "translateY(0)",
        boxShadow: "0 10px 22px rgba(255,79,184,0.28)",
        filter: "brightness(0.98)",
    },
}));

export default function GlobalHelpFab({
    zIndex = 20050,
    hideOn = ["/onboard", "/waiting-approval"],
    autoHideMs = 20 * 1000,
}) {
    const { pathname } = useLocation();
    const { t } = useTranslation();

    const { enabled, phone, message, title, whatsappText } =
        useSelector(selectHelpFab);

    const [autoOpen, setAutoOpen] = useState(false);
    const [pinnedOpen, setPinnedOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const fabRef = useRef(null);
    const arrowRef = useRef(null);

    const popupHoverRef = useRef(false);
    const hideTimerRef = useRef(null);

    const shouldHide = !enabled || hideOn.some((p) => pathname.startsWith(p));

    const isMobileView = useMediaQuery("(max-width: 767.98px)");

    const clearHideTimer = useCallback(() => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    }, []);

    const openWhatsAppDirect = useCallback(() => {
        if (!phone || typeof window === "undefined") return;
        const url = `https://wa.me/${phone}?text=${encodeURIComponent(whatsappText || "")}`;
        window.open(url, "_blank", "noopener,noreferrer");
    }, [phone, whatsappText]);

    const scheduleHide = useCallback(
        (delayMs) => {
            clearHideTimer();
            hideTimerRef.current = setTimeout(() => {
                if (popupHoverRef.current) {
                    scheduleHide(800);
                    return;
                }
                setAutoOpen(false);
                setPinnedOpen(false);
            }, delayMs);
        },
        [clearHideTimer]
    );

    useEffect(() => {
        if (shouldHide || isMobileView) {
            setAutoOpen(false);
            setPinnedOpen(false);
            clearHideTimer();
            return;
        }

        const el = fabRef.current;
        if (message && el) {
            setAnchorEl(el);
            setAutoOpen(true);
            scheduleHide(autoHideMs);
            return () => clearHideTimer();
        }

        setAutoOpen(false);
        clearHideTimer();
    }, [message, shouldHide, isMobileView, autoHideMs, scheduleHide, clearHideTimer]);

    const handleEnter = () => {
        if (isMobileView) return;
        if (fabRef.current) setAnchorEl(fabRef.current);
        if (message) {
            setAutoOpen(true);
            scheduleHide(autoHideMs);
        }
    };

    const handleLeave = () => {
        if (pinnedOpen) return;
        scheduleHide(350);
    };

    const handleFabClick = () => {
        if (isMobileView) {
            openWhatsAppDirect();
            return;
        }

        if (fabRef.current) setAnchorEl(fabRef.current);

        setPinnedOpen((v) => {
            const next = !v;
            if (next) {
                setAutoOpen(true);
                clearHideTimer();
            } else {
                setAutoOpen(false);
            }
            return next;
        });
    };

    const closeAll = () => {
        setAutoOpen(false);
        setPinnedOpen(false);
    };

    const onPopupEnter = () => {
        popupHoverRef.current = true;
        clearHideTimer();
        setAutoOpen(true);
    };

    const onPopupLeave = () => {
        popupHoverRef.current = false;
        if (pinnedOpen) return;
        scheduleHide(900);
    };

    const renderWhatsAppCta = () => {
        if (!phone) return null;

        return (
            <CtaLink
                href={`https://wa.me/${phone}?text=${encodeURIComponent(whatsappText || "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn d-inline-flex align-items-center gap-2 px-4 py-2 fw-semibold shadow-sm"
            >
                <WhatsApp />
                {t("setting.emission.custom.button")}
            </CtaLink>
        );
    };

    if (shouldHide) return null;

    const open = !isMobileView && ((autoOpen && !!message) || pinnedOpen);

    const shownTitle = title ? t(title) : t("help.defaultTitle");
    const shownMessage = message ? t(message) : t("help.defaultContent");

    return createPortal(
        <>
            <Badge
                color="info"
                variant="dot"
                overlap="circular"
                sx={{
                    position: "fixed",
                    right: "max(16px, env(safe-area-inset-right))",
                    bottom: "max(16px, env(safe-area-inset-bottom))",
                    zIndex,
                }}
            >
                <HelpFab
                    ref={fabRef}
                    color="primary"
                    aria-label="help"
                    onClick={handleFabClick}
                    onMouseEnter={handleEnter}
                    onFocus={handleEnter}
                    onMouseLeave={handleLeave}
                    onBlur={handleLeave}
                    sx={{ width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 } }}
                >
                    <SupportAgentIcon
                        fontSize="medium"
                        sx={{
                            opacity: 0.98,
                            color: "white"
                        }}
                    />
                </HelpFab>
            </Badge>

            <Popper
                open={open}
                anchorEl={anchorEl}
                placement={isMobileView ? "top" : "top-end"}
                transition
                modifiers={[
                    { name: "offset", options: { offset: [8, 16] } },
                    { name: "arrow", options: { element: arrowRef.current } },
                    {
                        name: "flip",
                        options: { fallbackPlacements: ["top", "top-end", "top-start"] },
                    },
                    {
                        name: "preventOverflow",
                        options: { boundary: "viewport", altAxis: true, padding: 8 },
                    },
                    { name: "shift", options: { padding: 8 } },
                ]}
                sx={{ zIndex: zIndex + 2 }}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        timeout={{ enter: 520, exit: 220 }}
                        style={{
                            transformOrigin: placement?.startsWith("top")
                                ? "right bottom"
                                : "right top",
                        }}
                    >
                        <Box onMouseEnter={onPopupEnter} onMouseLeave={onPopupLeave}>
                            <ClickAwayListener onClickAway={closeAll}>
                                <Box>
                                    <PopupCard elevation={0}>
                                        <span className="MuiPopper-arrow" ref={arrowRef} />
                                        <Stack spacing={0.9} sx={{ pr: 0 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                                {shownTitle}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                sx={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}
                                            >
                                                {shownMessage}
                                            </Typography>

                                            <Stack direction="row" sx={{ mt: 0.75 }} justifyContent="center">
                                                {renderWhatsAppCta()}
                                            </Stack>
                                        </Stack>
                                    </PopupCard>
                                </Box>
                            </ClickAwayListener>
                        </Box>
                    </Grow>
                )}
            </Popper>
        </>,
        document.body
    );
}
